import type { CurrentUser } from "./currentUser";

// ----------------------------------------------------------------------------

const SESSION_STORAGE_KEY = "gea_luce_auth_user";

export function getAuthenticatedUser(): CurrentUser | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!data) return null;
    return JSON.parse(data) as CurrentUser;
  } catch (error) {
    console.error("[Auth] Failed to parse session user:", error);
    return null;
  }
}

export function setAuthenticatedUser(user: CurrentUser): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.error("[Auth] Failed to save session user:", error);
  }
}

export function clearAuthenticatedUser(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error("[Auth] Failed to clear session user:", error);
  }
}

export function isUserAdminOrDev(user?: CurrentUser | null): boolean {
  if (!user) return false;
  return Boolean(
    user.canViewAll ||
      user.role === "admin" ||
      user.id?.toLowerCase() === "dev-nogravity",
  );
}

export function getActiveAgentParam(user?: CurrentUser | null): string {
  if (!user || isUserAdminOrDev(user)) return "";
  return user.agentTag || (user.agentCode ? `agente-${user.agentCode}` : "none");
}

export function filterClientsForUser<T extends { tags?: string[]; isAgent?: boolean }>(
  clients: T[],
  user?: CurrentUser | null,
): T[] {
  if (isUserAdminOrDev(user)) {
    return clients.filter((c) => !c.isAgent);
  }
  if (!user?.agentTag && !user?.agentCode) return [];
  const tagLower = user.agentTag ? user.agentTag.toLowerCase().trim() : "";
  const codeStr = user.agentCode ? user.agentCode.trim() : "";

  return clients.filter((c) => {
    if (c.isAgent) return false;
    const tags = (c.tags || []).map((t) => t.toLowerCase().trim());
    return (
      (tagLower && tags.includes(tagLower)) ||
      (codeStr &&
        tags.some(
          (t) =>
            t === `agente-${codeStr.toLowerCase()}` ||
            t === codeStr.toLowerCase(),
        ))
    );
  });
}
